import { useRouter } from 'next/router'
import { useCallback, useContext, useMemo, useState } from 'react'

import { AssetWithdrawalContext } from '@/components/base/AssetWithdrawal/context'
import { WrappedTokensFlowContext } from '@/components/base/WrappedTokensFlow/context'
import MethodSelectionDialog, {
  METHOD_TYPE,
} from '@/components/core-tokens-list/MethodSelectionDialog'
import useTokens from '@/hooks/use-tokens'
import type { CustomTokenInfo } from '@/utils/helpers'

const useAssetsMethodSelectionFlow = () => {
  const router = useRouter()
  const { fiatTokensList } = useTokens()

  const { setSelectedToken: setWithdrawToken } = useContext(AssetWithdrawalContext)
  const { setSelectedToken: setDepositToken } = useContext(WrappedTokensFlowContext)
  const [selectedDepositToken, setSelectedDepositToken] = useState<CustomTokenInfo | null>(null)
  const [selectedWithdrawToken, setSelectedWithdrawToken] = useState<CustomTokenInfo | null>(null)

  const isP2pSupported = useCallback(
    (tokenSymbol: string) => fiatTokensList.some((fiatToken) => tokenSymbol === fiatToken.symbol),
    [fiatTokensList],
  )

  const openDepositModal = useCallback(
    (token: CustomTokenInfo) => {
      setSelectedDepositToken(token)
    },
    [setSelectedDepositToken],
  )

  const openWithdrawModal = useCallback(
    (token: CustomTokenInfo) => {
      setSelectedWithdrawToken(token)
    },
    [setSelectedWithdrawToken],
  )

  const closeMethodSelectionDialog = useCallback(() => {
    setSelectedDepositToken(null)
    setSelectedWithdrawToken(null)
  }, [])

  const onClickMethod = useCallback(
    (type: string) => {
      if (type === METHOD_TYPE.network) {
        selectedDepositToken && setDepositToken(selectedDepositToken)
        selectedWithdrawToken && setWithdrawToken(selectedWithdrawToken)
      }

      if (type === METHOD_TYPE.bank) {
        selectedDepositToken &&
          (isP2pSupported(selectedDepositToken.symbol)
            ? router.push(`/buy/${selectedDepositToken.symbol}`)
            : router.push(`/reVND_${selectedDepositToken.symbol}`))
        selectedWithdrawToken &&
          (isP2pSupported(selectedWithdrawToken.symbol)
            ? router.push(`/sell/${selectedWithdrawToken.symbol}`)
            : router.push(`/${selectedWithdrawToken.symbol}_reVND`))
      }

      closeMethodSelectionDialog()
    },
    [
      router,
      selectedDepositToken,
      selectedWithdrawToken,
      setDepositToken,
      setWithdrawToken,
      closeMethodSelectionDialog,
      isP2pSupported,
    ],
  )

  return useMemo(() => {
    return {
      openDepositModal,
      openWithdrawModal,
      setSelectedDepositToken,
      setSelectedWithdrawToken,
      rendered: (
        <MethodSelectionDialog
          onClose={closeMethodSelectionDialog}
          selectedDepositToken={selectedDepositToken}
          selectedWithdrawToken={selectedWithdrawToken}
          onClick={onClickMethod}
          isP2pSupported={isP2pSupported}
        />
      ),
    }
  }, [
    closeMethodSelectionDialog,
    isP2pSupported,
    onClickMethod,
    openDepositModal,
    openWithdrawModal,
    selectedDepositToken,
    selectedWithdrawToken,
  ])
}

export default useAssetsMethodSelectionFlow
